/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
import React, {
  createContext,
  FC,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react"
import { omit } from "lodash"

import { EventAction, EventCategory } from "ecommerce/constants/tracking"
import { useMerchantDetail } from "ecommerce/hooks/useMerchantDetail"
import { getGrabXValueFromLocalStorage, MerchantPortalFeatureFlag } from "grabx"
import { storageHelper } from "utils/storage"
import { TroyWebTracker } from "utils/tracking"

import { PmfFeedbackPayload, PmfFeedbackState } from "./PmfFeedbackContent"
import { PmfFeedbackWrapper } from "./PmfFeedbackWrapper"
import {
  getPmfFeedbackUserIdentifier,
  incrementPmfFeedbackUserCount,
  markPmfFeedbackUserDone,
  PmfFeedbackUserIdentifier,
} from "./pmfFeedbackStorage"

/**
 * Events that can trigger the PMF feedback prompt.
 *
 * `GENERAL` is special: it always opens the feedback immediately and never
 * touches localStorage (the "user tapped Give Feedback" path). Every other
 * event shares one per-user counter in localStorage — see `pmfFeedbackStorage.ts`.
 */
export type PmfFeedbackEvent =
  | "GENERAL"
  | "ADD_PRODUCT"
  | "UPDATE_PRODUCT"
  | "UPDATE_STOCK"
  | "BULK_ADD_PRODUCT"
  | "BULK_UPDATE_STOCK"

export interface PmfFeedbackOpenOptions {
  event: PmfFeedbackEvent
}

interface PmfFeedbackContextValue {
  openPmfFeedback: (options: PmfFeedbackOpenOptions) => void
  closePmfFeedback: () => void
}

interface PmfFeedbackProviderProps {
  children: ReactNode
}

const PmfFeedbackContext = createContext<PmfFeedbackContextValue | undefined>(
  undefined,
)

const isEcomPmfFeedbackEnabled = (): boolean =>
  getGrabXValueFromLocalStorage<boolean>(
    MerchantPortalFeatureFlag.portalEnableEcomPmfFeedback,
  )

const getTrackerSentimentValue = (
  rating?: PmfFeedbackState["rating"],
): "UP" | "DOWN" | "" => {
  if (rating === "GOOD") return "UP"
  if (rating === "BAD") return "DOWN"
  return ""
}

export const PmfFeedbackProvider: FC<PmfFeedbackProviderProps> = ({
  children,
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const activeEventRef = useRef<PmfFeedbackEvent>()
  const activeUserIdentifierRef = useRef<PmfFeedbackUserIdentifier>()
  const activeEventCountRef = useRef<number>()
  const latestFormStateRef = useRef<PmfFeedbackState>()

  const { merchantGroupID, merchant } = useMerchantDetail()

  const generateDefaultTrackerLabel = useCallback(() => {
    return {
      MERCHANT_GROUP_ID: merchantGroupID,
      STORE_ID: merchant?.ID,
      COUNTRY: storageHelper.getUserCountry(),
      TRIGGERING_EVENT: activeEventRef.current,
      QUALIFYING_EVENT_COUNT: activeEventCountRef.current,
    }
  }, [merchantGroupID, merchant])

  const openFeedback = useCallback(() => {
    latestFormStateRef.current = undefined
    setIsOpen(true)
    TroyWebTracker.sendEvent({
      category: EventCategory.pmfFeedback,
      action: EventAction.feedbackPromptShown,
      labels: { ...generateDefaultTrackerLabel() },
    })
  }, [generateDefaultTrackerLabel])

  const clearActiveFeedbackSession = useCallback(() => {
    activeEventRef.current = undefined
    activeUserIdentifierRef.current = undefined
    activeEventCountRef.current = undefined
    latestFormStateRef.current = undefined
    setIsOpen(false)
  }, [])

  const handleFormStateChange = useCallback((state: PmfFeedbackState) => {
    latestFormStateRef.current = state
  }, [])

  const openPmfFeedback = useCallback(
    (options: PmfFeedbackOpenOptions) => {
      if (!isEcomPmfFeedbackEnabled()) return

      if (isOpen) return

      const { event } = options
      activeEventRef.current = event

      // GENERAL always opens immediately — no counting, no done-check, no localStorage.
      if (event === "GENERAL") {
        activeUserIdentifierRef.current = undefined
        activeEventCountRef.current = 0
        openFeedback()
        return
      }

      // Non-GENERAL events — gated per user by localStorage (single counter).
      const userIdentifier = getPmfFeedbackUserIdentifier()
      const {
        updatedState,
        didIncrement,
        showFeedback,
      } = incrementPmfFeedbackUserCount(userIdentifier)
      activeUserIdentifierRef.current = userIdentifier
      activeEventCountRef.current = updatedState.count
      if (didIncrement) {
        TroyWebTracker.sendEvent({
          category: EventCategory.pmfFeedback,
          action: EventAction.qualifyingEventCompleted,
          labels: {
            ...omit(generateDefaultTrackerLabel(), ["QUALIFYING_EVENT_COUNT"]),
            EVENT_COUNT: activeEventCountRef.current,
          },
        })
      }
      if (showFeedback) {
        openFeedback()
      }
    },
    [isOpen, openFeedback, generateDefaultTrackerLabel],
  )

  const closePmfFeedback = useCallback(() => {
    const form = latestFormStateRef.current
    TroyWebTracker.sendEvent({
      category: EventCategory.pmfFeedback,
      action: EventAction.feedbackPromptDismissed,
      labels: {
        ...generateDefaultTrackerLabel(),
        SENTIMENT: getTrackerSentimentValue(form?.rating),
        REASONS: form?.topics ?? [],
        FREE_TEXT: form?.comment ?? "",
      },
    })
    clearActiveFeedbackSession()
  }, [clearActiveFeedbackSession, generateDefaultTrackerLabel])

  const submitPmfFeedback = useCallback(
    (payload: PmfFeedbackPayload) => {
      const event = activeEventRef.current
      // Only counted (non-GENERAL) events write a "done" mark. GENERAL never
      // touches localStorage, so submitting from GENERAL does not suppress
      // future prompts.
      if (event && event !== "GENERAL") {
        const userIdentifier = activeUserIdentifierRef.current
        if (userIdentifier) markPmfFeedbackUserDone(userIdentifier)
      }
      TroyWebTracker.sendEvent({
        category: EventCategory.pmfFeedback,
        action: EventAction.feedbackSubmitted,
        labels: {
          ...generateDefaultTrackerLabel(),
          SENTIMENT: getTrackerSentimentValue(payload.rating),
          REASONS: payload.topics,
          FREE_TEXT: payload.comment,
        },
      })
      clearActiveFeedbackSession()
    },
    [clearActiveFeedbackSession, generateDefaultTrackerLabel],
  )

  const value = useMemo(
    () => ({
      openPmfFeedback,
      closePmfFeedback,
    }),
    [openPmfFeedback, closePmfFeedback],
  )

  return (
    <PmfFeedbackContext.Provider value={value}>
      {children}
      <PmfFeedbackWrapper
        open={isOpen}
        onClose={closePmfFeedback}
        onStateChange={handleFormStateChange}
        onSubmit={submitPmfFeedback}
      />
    </PmfFeedbackContext.Provider>
  )
}

export const usePmfFeedback = (): PmfFeedbackContextValue => {
  const context = useContext(PmfFeedbackContext)

  if (!context) {
    throw new Error("usePmfFeedback must be used within a PmfFeedbackProvider")
  }

  return context
}
